/*
    year: YYYY = 2021, YY = 21;
    month: MMMM = January, MMM = Jan, MM = 01, Mn = J, M = 1;
    day: DD: 01, D: 1;
    weekday: dddd: Monday, ddd: Mon, d: M;
    hour: H: 0 ... 23, HH: 00 ... 23, hh: 01 ... 12, h: 1 ... 12;
    minute: mm: 01, m: 1;
    second: ss: 01, s: 1;
*/

const masksMapping = {
    year: {YYYY: 'numeric', YY: '2-digit'},
    month: {MMMM: 'long', MMM: 'short', MM: '2-digit', Mn: 'narrow', M: 'numeric'},
    day: {DD: '2-digit', D: 'numeric'},
    weekday: {dddd: 'long', ddd: 'short', d: 'narrow'},
    hour: {HH: '2-digit', H: 'numeric', hh: '2-digit', h: 'numeric'},
    minute: {mm: '2-digit', m: 'numeric'},
    second: {ss: '2-digit', s: 'numeric'},
};

const itemsHour12 = ['hh', 'h'];
const masksMappingKeys = Object.keys(masksMapping);

/**
 * Format date according to provided mask
 *
 * @param {String} dateTime - String with date
 * @param {String} mask - Mask for formatting. See mapping above
 * @param {Number} timezoneOffset - Options for converting
 * @param {{isUTC: boolean, withGMTTimezone: boolean}} options - Formatting options
 * $return {String} Formatted date
 */
export function formatDateFromMask(dateTime, mask, timezoneOffset, options = {}) {
    const {isUTC = true, withGMTTimezone = false} = options;
    const dateObject = dateTime ? new Date(dateTime) : new Date();
    const {intlOptions, dateOptions} = getIntlAndDateOptions(mask, isUTC ? {timeZone: 'UTC'} : {});

    const hours = Math.trunc(timezoneOffset);
    const minutes = Math.round((timezoneOffset - hours) * 60);

    dateObject.setHours(dateObject.getHours() + hours);
    dateObject.setMinutes(dateObject.getMinutes() + minutes);

    const formatter = new Intl.DateTimeFormat('en-GB', intlOptions).formatToParts(dateObject);

    const formattedDateTime = formatter.reduce((reducer, formatterItem) => {
        const {type, value} = formatterItem;
        const dateOptionsItem = dateOptions[type];

        if (dateOptionsItem) {
            const pattern = new RegExp(`\\b${dateOptionsItem.type}\\b`, 'g');

            return reducer.replace(pattern, value);
        }

        return reducer;
    }, mask);

    if (withGMTTimezone) {
        const timezoneString = getTimeZoneGMTString(timezoneOffset);

        return `${formattedDateTime} ${timezoneString}`;
    }

    return formattedDateTime;
}

function getTimeZoneGMTString(timeZoneOffset = 0) {
    const sign = timeZoneOffset < 0 ? '' : '+';
    const hours = Math.trunc(timeZoneOffset);
    const minutes = Math.abs(60 * (timeZoneOffset - hours));
    const timeZoneString = minutes > 0 ? `${hours}:${minutes}` : hours;

    return `GMT${sign}${timeZoneString}`;
}

function getIntlAndDateOptions(mask, defaultIntlOptions = {}) {
    const intlOptions = {...defaultIntlOptions};
    const dateOptions = getDateOptions(mask);

    for (const dateOptionKey in dateOptions) {
        const {value: dateOptionValue} = dateOptions[dateOptionKey];

        intlOptions[dateOptionKey] = dateOptionValue;
    }

    if (dateOptions.hour && itemsHour12.includes(dateOptions.hour.type)) {
        intlOptions.hourCycle = 'h12';
    }

    return {intlOptions, dateOptions};
}

function getDateOptions(mask) {
    return masksMappingKeys.reduce((reducer, mapItemKey) => {
        const masksMapItem = masksMapping[mapItemKey];
        const masksMapItemKeys = Object.keys(masksMapItem);

        const masksItemType = masksMapItemKeys.find(key => mask.includes(key));

        if (masksItemType) {
            reducer[mapItemKey] = {
                type: masksItemType,
                value: masksMapItem[masksItemType],
            };
        }

        return reducer;
    }, {});
}
